<template>
  <div class="pb-1">
    <!-- Discovery Start-->
    <b-card>
      <header class="d-flex flex-wrap justify-content-between w-100 p-2">
        <label class="text__header mb-0">Discovery Node</label>
        <div class="d-flex">
          <atom-button
            buttonId="button__credential"
            :buttonStyle="`${hasCredential ? 'border' : 'filled'}`"
            @click="goToCredentialPage"
            class="mr-1"
            >{{ hasCredential ? "Credentials" : "Create Credentials" }}
          </atom-button>
          <atom-button
            buttonId="button__create-backup"
            :buttonStyle="`${hasBackup ? 'border' : 'filled'}`"
            @click="goToBackupSettingPage"
            >{{ hasBackup ? "Backup Settings" : "Create Backup" }}</atom-button
          >
        </div>
      </header>
      <main>
        <div class="discovery-node__content">
          <molecule-data-table
            v-model="inputSearchTable"
            :fields="fields"
            :items="items"
            :isCustomized="true"
            :isShowPagination="false"
            :enabledSelect="true"
            placeholderSearch="Filter Nodes"
            @onRowClick="selectRow"
            maxHeight="621px"
            @selectionChanged="selectionChanged"
          >
            <template #leftOfSearch>
              <div class="content__search">
                <div class="search__input">
                  <atom-input
                    title="Search by Subnet"
                    :isOnlyIcon="true"
                    placeholder="101.0.1.0/24"
                    v-model="inputSearchSubnet"
                    :isDisable="isDisableInputSearch"
                  ></atom-input>
                </div>

                <atom-button
                  buttonId="button-search"
                  :isDisable="isDisableButtonSearch"
                  iconLeft="SearchIcon"
                  @click="clickButtonSearch"
                  class="search__button"
                >
                </atom-button>
              </div>
            </template>

            <template #emptyData>
              <div class="content__empty-data">
                <feather-icon
                  icon="CloudOffIcon"
                  class="empty-data__icon"
                ></feather-icon>
                <label class="empty-data__text--title">No Equipment</label>
                <label class="empty-data__text--body"
                  >Please Search Subnet</label
                >
              </div>
            </template>

            <template #nodeName="data">
              <span class="text__node-name">{{ data.row.nodeName }}</span>
            </template>
            <template #ipAddress="data">
              <span class="text__body-table">{{ data.row.ipAddress }}</span>
            </template>
            <template #description="data">
              <span class="text__body-table">{{ data.row.description }}</span>
            </template>
            <template #sshCredential="data">
              <atom-select
                v-model="data.row.sshCredential"
                :option="sshCredentialOptions"
                label="name"
                :isClearable="false"
                :isDisable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :isError="isErrorSelect(data.row, data.row.sshCredential)"
                placeholder="SSH Credential"
              ></atom-select>
            </template>
            <template #autoBackupCommand="data">
              <atom-select
                v-model="data.row.autoBackupCommand"
                :option="autoBackupCommandOptions"
                label="name"
                :isClearable="false"
                :isDisable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :isError="isErrorSelect(data.row, data.row.autoBackupCommand)"
                placeholder="Backup Command"
              ></atom-select>
            </template>
          </molecule-data-table>
        </div>

        <div class="discovery-node__footer footer__top--content">
          <label class="footer__text text-secondary">
            Showing {{ totalCount }} entries
          </label>
        </div>
      </main>
      <hr />
      <footer class="p-2">
        <div>
          <span>Tag (optional)</span>
          <atom-multi-select
            v-model="selectedTagList"
            :option="tagOptions"
            :isPushTaggable="true"
            :isDisable="isDisableTags"
            placeholder="Select Tag"
          ></atom-multi-select>
        </div>
        <div class="w-100 d-flex justify-content-between align-items-end my-1">
          <div>
            <b v-if="isError" class="text-danger">
              Select Credential and Backup Command for every selected node
            </b>
          </div>
          <atom-button
            buttonId="button__add-equipment"
            :isDisable="isDisableButtonAddEquipment"
            @click="clickAddEquipment"
            >Add Equipment</atom-button
          >
        </div>
      </footer>
    </b-card>
    <!-- Discovery End-->

    <!-- Modal Start-->
    <section name="modal">
      <molecule-modal-dialog
        modalId="discovery__confirm-add"
        :isTextCenter="true"
        textTitle="Do you want to add equipment?"
        :isSlotBody="true"
        buttonTextLeft="Confirm"
        @clickButtonLeft="clickConfirmAddEquipment"
      >
        <template #body>
          <div class="px-2 pt-1 w-100">
            <div></div>
            <div>
              <span>Tag (optional)</span>
              <atom-multi-select
                v-model="selectedTagList"
                :option="tagOptions"
                :isPushTaggable="true"
                :isDisable="isDisableTags"
                placeholder="Select Tag"
              ></atom-multi-select>

              <div class="mt-2">
                <label class="text__normal"
                  >Apply tags to equipment list below</label
                >
                <div
                  v-for="(equipment, index) in selectedEquipmentList"
                  :key="index"
                  class="d-flex align-items-center"
                >
                  <div class="dot"></div>
                  <label class="text__list">{{ equipment.nodeName }}</label>
                </div>
              </div>
            </div>
          </div>
        </template>
      </molecule-modal-dialog>
    </section>
    <!-- Modal End-->
  </div>
</template>

<script>
import { BCard } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import MoleculeDataTable from "@/components/molecules/MoleculeDataTable.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"

export default {
  name: "OrganismDiscovery",
  components: {
    BCard,
    AtomButton,
    AtomInput,
    AtomSelect,
    AtomMultiSelect,
    MoleculeDataTable,
    MoleculeModalDialog,
  },
  props: {
    hasCredential: {
      type: Boolean,
      required: true,
    },
    hasBackup: {
      type: Boolean,
      required: true,
    },
    fields: {
      type: Array,
      required: false,
      default: [],
    },
    discoveryList: {
      type: Array,
      required: false,
      default: [],
    },
    totalCount: {
      type: Number,
      required: false,
      default: 0,
    },
    sshCredentialOptions: {
      type: Array,
      required: false,
      default: [],
    },
    autoBackupCommandOptions: {
      type: Array,
      required: false,
      default: [],
    },
    tagOptions: {
      type: Array,
      required: false,
      default: [],
    },
  },
  emits: ["selectionChanged", "searchBySubnet", "addEquipment"],
  data() {
    return {
      modalId: null,

      items: [],

      inputSearchSubnet: "",
      inputSearchTable: "",

      selectedEquipmentList: [],
      selectedTagList: [],

      isValidate: false,
      isError: false,
    }
  },
  computed: {
    isDisableButtonAddEquipment() {
      return (
        this.selectedEquipmentList.length === 0 ||
        (this.isValidate && this.isError)
      )
    },

    isDisableTags() {
      return this.totalCount === 0
    },

    isDisableInputSearch() {
      return !this.hasCredential || !this.hasBackup
    },
    isDisableButtonSearch() {
      if (!this.inputSearchSubnet) return true
      else return (!this.hasCredential || !this.hasBackup)
    },
  },
  watch: {
    discoveryList: {
      deep: true,
      handler() {
        this.items = JSON.parse(JSON.stringify(this.discoveryList))
      },
    },
    selectedEquipmentList: {
      deep: true,
      handler() {
        this.validateError()
      },
    },
  },
  mounted() {
    this.items = JSON.parse(JSON.stringify(this.discoveryList))
  },
  methods: {
    goToCredentialPage() {
      this.$router.push({ name: "networkConfigurationCredential" })
    },
    goToBackupSettingPage() {
      this.$router.push({ name: "networkConfigurationBackupSettings" })
    },

    clickButtonSearch() {
      this.$emit("searchBySubnet", this.inputSearchSubnet)
    },
    selectRow() {},
    selectionChanged(equipmentList) {
      this.selectedEquipmentList = equipmentList

      this.$emit("selectionChanged", this.selectedEquipmentList)
    },

    clickAddEquipment() {
      this.isValidate = true

      this.validateError()

      if (!this.isError) this.openModal("discovery__confirm-add")
    },
    clickConfirmAddEquipment() {
      const nodes = []
  
      this.selectedEquipmentList.forEach(equipment => {
        nodes.push({
          hostname: equipment.nodeName,
          description: equipment.description,
          ipAddress: equipment.ipAddress,
          snmpCredentialId: equipment.snmpCredentialId,
          remoteCredentialId: equipment.sshCredential.id,
          autoBackupConfigId: equipment.autoBackupCommand.id,
        })
      })

      const addEquipment = {
        tags: this.selectedTagList,
        nodes
      }

      this.$emit("addEquipment", addEquipment)

      this.closeModal()
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    // close modal
    closeModal(id) {
      if (id) this.modalId = id
      this.$bvModal.hide(this.modalId)
      this.modalId = ""
    },

    validateError() {
      if (this.selectedEquipmentList.length && this.isValidate)
        this.isError =
          this.selectedEquipmentList.filter(
            (equipment) =>
              !equipment.sshCredential || !equipment.autoBackupCommand
          ).length !== 0
      else this.isError = false
    },

    isErrorSelect(item, check) {
      if (this.selectedEquipmentList.length && this.isValidate && this.isError)
        return (
          !check &&
          this.selectedEquipmentList.find(
            (selected) => JSON.stringify(selected) === JSON.stringify(item)
          ) !== undefined
        )

      return false
    },
  },
}
</script>

<style lang="scss" scoped>
hr {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

.card-body {
  padding: 0;
}

.card-padding {
  padding: 1.5rem;
}

.text__header {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
}

.text__node-name {
  color: #6e6b7b;
  font-weight: 600;
  font-size: 14px;
}

.text__body-table {
  color: #6e6b7b;
  font-weight: 400;
  font-size: 14px;
}

.text__normal {
  color: #6e6b7b;
  font-weight: 400;
  font-size: 12px;
}

.dot {
  height: 8px;
  width: 8px;
  background-color: #bbb;
  border-radius: 50%;
  margin-right: 10px;
  margin-bottom: 4px;
}

.text__list {
  color: #6e6b7b;
  font-weight: 400;
  font-size: 14px;
}

.content__search {
  display: flex;
  align-items: flex-end;
  width: 100%;
  margin-bottom: 10px;
}

.search__input {
  width: 100%;
}
.search__button {
  margin-left: 10px;
}
.content__empty-data {
  height: 510px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}
.empty-data__text--body {
  color: #b9b9c3;
  font-size: 14px;
  margin-top: 10px;
}

.discovery-node__footer {
  padding-top: 30px;
  flex-wrap: wrap;
}
.footer__text {
  padding-left: 10px;
  font-size: 14px;
}

.footer__top--content {
  padding: 1.5rem 1.5rem 0 1.5rem;
}

.discovery-node__content ::v-deep {
  .molecule-datatable--header-search-container {
    padding: 21px 21px 0 21px;
  }
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
  .content__search {
    margin-bottom: 0;
    width: fit-content;
  }
  .search__input {
    width: 330px;
  }
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .search__input {
    width: 378px;
  }

  .discovery-node__content ::v-deep {
    .molecule-datatable--header-search-container {
      justify-content: space-between;
      padding: 0 22px 0 22px;
    }
  }
}
</style>
